import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { memo, useState, useRef } from "react";
import PropTypes from "prop-types";
import { equals, pick } from "ramda";
import { Flex, SearchInput, Checkbox } from "@base-ui";
import { Options, Option } from "./multiselect.styles";
import { useOpen, useClickOutside } from "hooks";
import handleKeyDown from "./handleKeyDown";
import { blockDefaultEvent } from "utils/events";
import { extendedSystemStylesProps } from "@mixins";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var styledSystemKeys = ["space", "layout", "flexbox"];
var MultiSelect = /*#__PURE__*/memo(function (props) {
  var id = props.id,
    label = props.label,
    options = props.options,
    hideLabel = props.hideLabel,
    _props$searchString = props.searchString,
    searchString = _props$searchString === void 0 ? "" : _props$searchString,
    search = props.search,
    _onChange = props.onChange,
    error = props.error,
    placeholder = props.placeholder,
    className = props.className,
    disabled = props.disabled,
    disabledLabel = props.disabledLabel;
  var _useState = useState(-1),
    _useState2 = _slicedToArray(_useState, 2),
    activeFocus = _useState2[0],
    setActiveFocus = _useState2[1]; //idx
  var _useOpen = useOpen(false),
    open = _useOpen.open,
    setOpen = _useOpen.setOpen;
  var containerRef = useRef();
  var optionsRef = useRef();
  var searchRef = useRef();
  var handleSelect = function handleSelect(e) {
    blockDefaultEvent(e);
    var option = options[activeFocus];
    if (!option.disabled) _onChange(!option.checked, option.id);
  };
  handleKeyDown(id, containerRef, optionsRef, searchRef, open, setOpen, activeFocus, setActiveFocus, searchString, search, options);
  useClickOutside(containerRef, function () {
    return setOpen(false);
  }, open);
  var handleSearchString = function handleSearchString(string) {
    if (!open && string.length > 2) setOpen(true);else if (open && string.length < 3) setOpen(false);
    search(string);
  };
  var optionsMarkup = options.map(function (option, idx) {
    return /*#__PURE__*/_jsx(Option, {
      id: "".concat(id, "-option-").concat(idx),
      role: "option",
      "aria-checked": option.checked,
      "aria-disabled": option.disabled,
      focused: activeFocus === idx,
      children: /*#__PURE__*/_jsx(Checkbox, {
        id: "".concat(id, "-checkbox-").concat(idx),
        label: option.label,
        checked: option.checked,
        disabled: option.disabled,
        onChange: function onChange(add) {
          return _onChange(add, option.id);
        },
        ellipse: true,
        tabIndex: -1
      })
    }, option.id);
  });
  var activeDescendant = activeFocus !== -1 ? "".concat(id, "-option-").concat(activeFocus) : undefined;
  return /*#__PURE__*/_jsx(Flex.Row, _objectSpread(_objectSpread({
    className: className
  }, extendedSystemStylesProps(styledSystemKeys, props)), {}, {
    children: /*#__PURE__*/_jsxs(Flex.Column, {
      ref: containerRef,
      style: {
        position: "relative"
      },
      children: [/*#__PURE__*/_jsx(SearchInput, {
        searchRef: searchRef,
        id: "".concat(id, "-input"),
        ariaHasPopup: "listbox",
        error: error,
        ariaExpanded: open,
        ariaControls: "".concat(id, "-listbox"),
        ariaAutocomplete: "list",
        value: searchString,
        label: !disabled ? label : disabledLabel,
        search: handleSearchString,
        placeholder: placeholder,
        disabled: disabled,
        onFocus: function onFocus() {
          return options.length > 0 && !open && setOpen(true);
        },
        title: disabled ? disabledLabel : undefined,
        hideLabel: hideLabel,
        icon: "search"
      }), /*#__PURE__*/_jsx(Options, {
        id: "".concat(id, "-listbox"),
        "aria-activedescendant": activeDescendant,
        "aria-labelledby": "".concat(id, "-input-label"),
        role: "listbox",
        open: open && options.length > 0,
        onKeyDown: function onKeyDown(e) {
          return e.code === "Space" && handleSelect(e);
        },
        tabIndex: -1,
        ref: optionsRef,
        children: optionsMarkup
      })]
    })
  }));
}, function (prev, curr) {
  return equals(pick(["options", "searchString", "disabled", "error"], prev), pick(["options", "searchString", "disabled", "error"], curr));
});
MultiSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  searchString: PropTypes.string.isRequired,
  search: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  disabledLabel: PropTypes.string
};
export default MultiSelect;